/**
 * Case forms entered in BlueHQ and printed like the paper forms: the rights (Miranda) form, the custodial promise,
 * and the NCIC entry/deletion request (with the 911 / records operator's queue).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { FORM_LABEL, NCIC_PERSON_KINDS, NCIC_PROPERTY_STATUS, NCIC_PROPERTY_TYPES, RIGHTS_LINES, RIGHTS_OUTCOMES, RIGHTS_WAIVER, CUSTODIAL_PROMISE_TEXT, US_STATES, type FormKind } from '@civilos/domain';
import { api } from '../shared/api.ts';
import { chargeWords, StatutePicker } from '../shared/StatutePicker.tsx';
import { LicenseScanButton } from '../shared/LicenseScan.tsx';
import { useRouter } from '../shared/router.tsx';
import { Badge, Card, ErrorBox, Notice, PageHeader, Spinner, useAction, useLoad } from '../shared/ui.tsx';
import { at, EmptyState, when } from './common.tsx';
import { Box, Check, fmtD, fmtDT, fmtT, Head, Line, PrintSheet } from './printsheet.tsx';

const STATUS_LABEL: Record<string, string> = { draft: 'Draft', done: 'Done', sent: 'Sent to NCIC operator', entered: 'Entered in NCIC', rejected: 'Sent back', void: 'Void' };
const STATUS_TONE: Record<string, 'neutral' | 'ok' | 'warn' | 'bad' | 'info'> = { draft: 'neutral', done: 'ok', sent: 'warn', entered: 'ok', rejected: 'bad', void: 'neutral' };
const kindTitle = (f: any) => (f.kind === 'ncic' ? (f.ncic_action === 'deletion' ? 'NCIC deletion' : 'NCIC entry') : FORM_LABEL[f.kind as FormKind]);
const nowLocal = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};
const query = () => new URLSearchParams(location.search);

export function FormsPage() {
  const { navigate } = useRouter();
  const [box, setBox] = useState(query().get('box') ?? 'mine');
  const [kind, setKind] = useState('');
  const [q, setQ] = useState('');
  const r = useLoad(() => api.get<any>(`/api/forms?box=${box}&kind=${kind}&q=${encodeURIComponent(q.trim())}`), [box, kind, q]);
  const tabs: [string, string][] = [['mine', 'My forms']];
  if (r.data?.isOperator) tabs.push(['queue', `NCIC to enter${r.data.queue ? ` (${r.data.queue})` : ''}`]);
  tabs.push(['entered', 'Still in NCIC']);
  if (r.data?.seesAll || r.data?.isOperator) tabs.push(['all', 'All']);
  return (
    <div className="ops stack">
      <PageHeader
        crumb="Records"
        title="Forms"
        sub="Rights forms, custodial promises and NCIC requests: fill them in here, get the signatures on your screen, and print them like the paper forms."
        actions={
          r.data?.canWrite && (
            <div className="row gap-sm wrap">
              <button className="btn primary" onClick={() => navigate(at('/forms/new?kind=rights'))}>
                Rights form
              </button>
              <button className="btn primary" onClick={() => navigate(at('/forms/new?kind=custodial'))}>
                Custodial promise
              </button>
              <button className="btn primary" onClick={() => navigate(at('/forms/new?kind=ncic'))}>
                NCIC request
              </button>
            </div>
          )
        }
      />
      <div className="row between wrap gap">
        <div className="seg-ctl" role="tablist" aria-label="Which forms">
          {tabs.map(([k, l]) => (
            <button key={k} type="button" role="tab" aria-selected={box === k} className={box === k ? 'on' : ''} onClick={() => setBox(k)}>
              {l}
            </button>
          ))}
        </div>
        <div className="row gap-sm">
          <select value={kind} onChange={(e) => setKind(e.target.value)} aria-label="Kind of form">
            <option value="">Every form</option>
            <option value="rights">Rights forms</option>
            <option value="custodial">Custodial promises</option>
            <option value="ncic">NCIC requests</option>
          </select>
          <input type="search" className="rp-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, case #, form #, NIC #" aria-label="Search forms" />
        </div>
      </div>
      {box === 'entered' && <Notice tone="info">NCIC entries that haven’t been taken out. When the person is found or the property is recovered, open the entry and request the deletion.</Notice>}
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : !r.data ? (
        <Spinner />
      ) : !r.data.forms.length ? (
        <EmptyState icon="clipboard" title={box === 'queue' ? 'Nothing waiting to be entered' : 'No forms'} />
      ) : (
        <div className="card table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Form</th>
                <th>Kind</th>
                <th>Who / what</th>
                <th>Case #</th>
                <th>By</th>
                <th>When</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {r.data.forms.map((f: any) => (
                <tr key={f.id} className="clickable" onClick={() => navigate(at(`/forms/${f.id}`))}>
                  <td className="mono">{f.number}</td>
                  <td>{kindTitle(f)}</td>
                  <td>{f.subject_name ?? '—'}</td>
                  <td className="mono">{f.case_number ?? '—'}</td>
                  <td>{f.by_name}</td>
                  <td>{when(f.completed_at ?? f.created_at)}</td>
                  <td>
                    <Badge tone={STATUS_TONE[f.status]} dot>
                      {STATUS_LABEL[f.status]}
                    </Badge>
                    {f.nic_number && <span className="small mono"> {f.nic_number}</span>}
                    {f.deleted_by_number && <span className="small"> · deletion {f.deleted_by_status === 'entered' ? 'done' : 'requested'}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Start a form; optionally fill it from a report (pick the person or vehicle). */
export function NewForm() {
  const { navigate } = useRouter();
  const kind = (query().get('kind') ?? 'rights') as FormKind;
  const [cn, setCn] = useState(query().get('case') ?? '');
  const [found, setFound] = useState<any>(null);
  const look = useAction();
  const go = useAction();
  const start = (data: any, caseNumber?: string) => go.run(async () => navigate(at(`/forms/${(await api.post<{ id: string }>('/api/forms', { kind, caseNumber: caseNumber || cn.trim() || undefined, data })).id}`), true));
  const fromPerson = (p: any) => {
    const now = nowLocal();
    if (kind === 'rights') return { name: p.name, address: p.address, dob: p.dob, location: found?.location ?? '', readAt: now };
    if (kind === 'custodial') return { childName: p.name, childDob: p.dob, childAddress: p.address, charges: p.charges };
    const ht = p.height ? `${Math.floor(p.height / 12)}${String(p.height % 12).padStart(2, '0')}` : '';
    return { personKind: p.roles.includes('arrestee') || p.roles.includes('suspect') ? 'wanted' : null, person: { name: [p.last, p.first, p.middle].filter(Boolean).join(', ') || p.name, race: p.race, sex: p.sex, age: p.age, dob: p.dob, ht, wt: p.weight ? String(p.weight) : '', hair: p.hair, eyes: p.eyes, charges: (p.charges ?? []).join('; '), marks: p.marks } };
  };
  return (
    <div className="ops stack">
      <PageHeader crumb="Forms" title={`New ${FORM_LABEL[kind].toLowerCase()}`} sub="Fill it from a report, or start a blank one." />
      <Card title="From a report">
        <form
          className="row gap-sm wrap"
          onSubmit={(e) => {
            e.preventDefault();
            look.run(async () => setFound(await api.get(`/api/forms/from-case?case=${encodeURIComponent(cn.trim())}`)));
          }}
        >
          <input value={cn} onChange={(e) => setCn(e.target.value)} placeholder="Case # (2026-000123)" aria-label="Case number" className="mono" />
          <button className="btn" disabled={look.busy || !cn.trim()}>
            Look it up
          </button>
        </form>
        <ErrorBox error={look.error} />
        {found && (
          <div className="stack sm" style={{ marginTop: 10 }}>
            <div className="small muted">Case {found.caseNumber}{found.location ? ` · ${found.location}` : ''} — pick who this form is for:</div>
            {(kind === 'custodial' ? found.people.filter((p: any) => p.dob && Date.now() - +new Date(p.dob) < 18 * 365.25 * 86_400_000) : found.people).map((p: any) => (
              <button key={p.id} type="button" className="btn fm-pick" disabled={go.busy} onClick={() => start(fromPerson(p), found.caseNumber)}>
                <strong>{p.name}</strong>
                <span className="small muted">
                  {[p.roles.join(', '), p.dob && `DOB ${fmtD(p.dob)}`, p.charges?.length ? p.charges.join('; ') : null].filter(Boolean).join(' · ')}
                </span>
              </button>
            ))}
            {kind === 'ncic' &&
              [...found.vehicles, ...found.guns].map((v: any) => (
                <button
                  key={v.id}
                  type="button"
                  className="btn fm-pick"
                  disabled={go.busy}
                  onClick={() =>
                    start(
                      v.serial !== undefined
                        ? { propertyType: 'gun', propertyStatus: 'stolen', property: { serial: v.serial, brand: v.make, model: v.model } }
                        : { propertyType: 'vehicle', propertyStatus: 'stolen', property: { vinOrPlate: v.vin || v.plate, year: v.year, make: v.make, model: v.model, color: v.color, state: 'SC' } },
                      found.caseNumber,
                    )
                  }
                >
                  <strong>{v.description || [v.year, v.make, v.model].filter(Boolean).join(' ')}</strong>
                  <span className="small muted">{[v.plate, v.vin, v.serial].filter(Boolean).join(' · ')}</span>
                </button>
              ))}
            {kind === 'custodial' && !found.people.some((p: any) => p.dob && Date.now() - +new Date(p.dob) < 18 * 365.25 * 86_400_000) && <p className="small muted">No one under 18 (by date of birth) on this report.</p>}
          </div>
        )}
      </Card>
      <ErrorBox error={go.error} />
      <div>
        <button className="btn primary" disabled={go.busy} onClick={() => start(kind === 'rights' ? { readAt: nowLocal() } : {})}>
          Start a blank one
        </button>
      </div>
    </div>
  );
}

// ─── Signatures drawn on the screen ────────────────────────────────────────
function SignBox({ formId, slot, label, image, onSaved, small, disabled, name }: { formId: string; slot: string; label: string; image?: string | null; onSaved: () => void; small?: boolean; disabled?: boolean; name?: string }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const [redo, setRedo] = useState(!image);
  const a = useAction();
  const w = small ? 220 : 560;
  const hgt = small ? 80 : 150;
  useEffect(() => setRedo(!image), [image]);
  useEffect(() => {
    if (!redo || disabled) return;
    const c = ref.current;
    if (!c) return;
    const g = c.getContext('2d')!;
    g.lineWidth = small ? 2.4 : 2.6;
    g.lineCap = 'round';
    g.strokeStyle = '#111';
    let down = false;
    let drew = false;
    const pos = (e: PointerEvent) => {
      const r = c.getBoundingClientRect();
      return [((e.clientX - r.left) * c.width) / r.width, ((e.clientY - r.top) * c.height) / r.height] as const;
    };
    const d = (e: PointerEvent) => {
      down = true;
      c.setPointerCapture(e.pointerId);
      const [x, y] = pos(e);
      g.beginPath();
      g.moveTo(x, y);
    };
    const m = (e: PointerEvent) => {
      if (!down) return;
      const [x, y] = pos(e);
      g.lineTo(x, y);
      g.stroke();
      drew = true;
    };
    const u = () => {
      down = false;
    };
    c.addEventListener('pointerdown', d);
    c.addEventListener('pointermove', m);
    c.addEventListener('pointerup', u);
    (c as any)._drew = () => drew;
    return () => {
      c.removeEventListener('pointerdown', d);
      c.removeEventListener('pointermove', m);
      c.removeEventListener('pointerup', u);
    };
  }, [redo, disabled, small]);
  const save = () =>
    a.run(async () => {
      const c = ref.current!;
      if (!(c as any)._drew?.()) throw new Error('Sign in the box first');
      await api.post(`/api/forms/${formId}/sign`, { slot, image: c.toDataURL('image/png'), name: name || undefined });
      onSaved();
    });
  return (
    <div className={`fm-sign${small ? ' small' : ''}`}>
      <div className="small strong">{label}</div>
      {!redo && image ? (
        <div className="row gap-sm">
          <img className="fm-sigimg" src={image} alt={`${label} — signed`} style={{ width: w / (small ? 1.3 : 2), height: hgt / (small ? 1.3 : 2) }} />
          {!disabled && (
            <button type="button" className="btn small ghost" onClick={() => a.run(async () => (await api.post(`/api/forms/${formId}/sign`, { slot, image: null }), setRedo(true), onSaved()))}>
              Redo
            </button>
          )}
        </div>
      ) : disabled ? (
        <div className="small muted">Not signed</div>
      ) : (
        <>
          <canvas ref={ref} width={w} height={hgt} className="fm-canvas" style={{ maxWidth: w }} aria-label={`${label}: sign here`} />
          <div className="row gap-sm">
            <button type="button" className="btn small primary" disabled={a.busy} onClick={save}>
              {small ? 'Save initials' : 'Save signature'}
            </button>
            <button
              type="button"
              className="btn small ghost"
              onClick={() => {
                const c = ref.current!;
                c.getContext('2d')!.clearRect(0, 0, c.width, c.height);
                (c as any)._drew = () => false;
                setRedo(false);
                setTimeout(() => setRedo(true));
              }}
            >
              Clear
            </button>
          </div>
        </>
      )}
      <ErrorBox error={a.error} />
    </div>
  );
}

// ─── One form: edit (draft), read, operator, print ─────────────────────────
export function FormDetail({ id }: { id: string }) {
  const { navigate } = useRouter();
  const r = useLoad(() => api.get<any>(`/api/forms/${id}`), [id]);
  const a = useAction();
  if (!r.data) return r.error ? <ErrorBox error={r.error} /> : <Spinner />;
  const { form: f, can } = r.data;
  return (
    <div className="ops stack">
      <PageHeader
        crumb={
          <button type="button" className="btn link" onClick={() => navigate(at('/forms'))}>
            Forms
          </button>
        }
        title={`${kindTitle(f)} ${f.number}`}
        sub={[f.subject_name, f.case_number && `case ${f.case_number}`, `${f.by_name} · ${when(f.created_at)}`].filter(Boolean).join(' · ')}
        actions={
          <div className="row gap-sm">
            <Badge tone={STATUS_TONE[f.status]} dot>
              {STATUS_LABEL[f.status]}
            </Badge>
            <button className="btn" onClick={() => navigate(at(`/forms/${id}/print`))}>
              Print
            </button>
          </div>
        }
      />
      {f.status === 'rejected' && <Notice tone="bad">Sent back by {f.operator_name}: {f.operator_note}. Start a new request with the fix.</Notice>}
      {f.kind === 'ncic' && f.status === 'entered' && (
        <Notice tone="ok">
          {f.ncic_action === 'deletion' ? 'Deleted from NCIC' : 'Entered in NCIC'} by {f.operator_name} {when(f.operator_at)}
          {f.nic_number ? ` · NIC ${f.nic_number}` : ''}
          {f.deleted_by_number ? ` · deletion ${f.deleted_by_number} ${f.deleted_by_status === 'entered' ? 'done' : 'requested'}` : ''}
        </Notice>
      )}
      {f.deletes_number && <Notice tone="info">This request takes out NCIC entry {f.deletes_number}.</Notice>}
      {can.operate && <OperatorPanel form={f} onDone={r.reload} />}
      {can.requestDeletion && (
        <Card title="Found or recovered?">
          <p className="small">When the person is located or the property is recovered, the NCIC entry has to come out. This starts the deletion request for the operator.</p>
          <button
            className="btn primary"
            disabled={a.busy}
            onClick={() => {
              const reason = window.prompt('Why should it come out? (located, recovered, warrant served…)');
              if (reason !== null) a.run(async () => navigate(at(`/forms/${(await api.post<{ id: string }>('/api/forms', { kind: 'ncic', deletesId: id, reason })).id}`)));
            }}
          >
            Request NCIC deletion
          </button>
        </Card>
      )}
      <ErrorBox error={a.error} />
      <FormEditor key={`${f.id}-${f.status}`} data={r.data} reload={r.reload} />
      {can.void && f.status !== 'draft' && (
        <div>
          <button
            className="btn ghost small"
            onClick={() => {
              const reason = window.prompt('Why void this form?');
              if (reason) a.run(async () => (await api.post(`/api/forms/${id}/void`, { reason }), r.reload()));
            }}
          >
            Void this form
          </button>
        </div>
      )}
    </div>
  );
}

function OperatorPanel({ form: f, onDone }: { form: any; onDone: () => void }) {
  const [nic, setNic] = useState('');
  const [note, setNote] = useState('');
  const a = useAction();
  return (
    <Card title={f.ncic_action === 'deletion' ? 'NCIC operator: take it out' : 'NCIC operator: enter it'}>
      <div className="stack sm">
        {f.ncic_action === 'entry' && (
          <label>
            NIC number from NCIC
            <input className="mono" value={nic} onChange={(e) => setNic(e.target.value)} placeholder="V123456789" />
          </label>
        )}
        <label>
          Note (needed to send it back)
          <input value={note} onChange={(e) => setNote(e.target.value)} />
        </label>
        <ErrorBox error={a.error} />
        <div className="row gap-sm">
          <button className="btn primary" disabled={a.busy} onClick={() => a.run(async () => (await api.post(`/api/forms/${f.id}/operator`, { action: 'entered', nicNumber: nic || undefined, note: note || undefined }), onDone()))}>
            {f.ncic_action === 'deletion' ? 'Deleted from NCIC' : 'Entered in NCIC'}
          </button>
          <button className="btn ghost" disabled={a.busy} onClick={() => a.run(async () => (await api.post(`/api/forms/${f.id}/operator`, { action: 'rejected', note }), onDone()))}>
            Send it back to the deputy
          </button>
        </div>
      </div>
    </Card>
  );
}

/** Saves a draft as the deputy types (data, case #, SSN only when it was changed). */
function useFormSave(id: string, enabled: boolean) {
  const [state, setState] = useState<'saving' | 'saved' | 'error' | null>(null);
  const pending = useRef<any>(null);
  const timer = useRef<any>(null);
  const flush = useCallback(async () => {
    clearTimeout(timer.current);
    const body = pending.current;
    if (!body) return;
    pending.current = null;
    setState('saving');
    try {
      await api.post(`/api/forms/${id}`, body);
      setState('saved');
    } catch (e) {
      setState('error');
      throw e;
    }
  }, [id]);
  const queue = useCallback(
    (body: any) => {
      if (!enabled) return;
      pending.current = { ...(pending.current ?? {}), ...body };
      clearTimeout(timer.current);
      timer.current = setTimeout(() => flush().catch(() => undefined), 700);
    },
    [enabled, flush],
  );
  return { state, queue, flush };
}

function FormEditor({ data, reload }: { data: any; reload: () => void }) {
  const { form: f, can, signatures, ssn } = data;
  const [d, setD] = useState<any>(f.data ?? {});
  const [cn, setCn] = useState(f.case_number ?? '');
  const [ssnText, setSsnText] = useState('');
  const sv = useFormSave(f.id, can.edit);
  const fin = useAction();
  const ro = !can.edit;
  const upd = (patch: any) => {
    const next = { ...d, ...patch };
    setD(next);
    sv.queue({ data: next });
  };
  const sub = (k: string, patch: any) => upd({ [k]: { ...(d[k] ?? {}), ...patch } });
  const inp = (k: string, label: string, opts: { type?: string; span?: number; mono?: boolean; ph?: string } = {}) => (
    <label style={opts.span ? { gridColumn: `span ${opts.span}` } : undefined}>
      {label}
      <input type={opts.type ?? 'text'} className={opts.mono ? 'mono' : ''} value={d[k] ?? ''} disabled={ro} placeholder={opts.ph} onChange={(e) => upd({ [k]: e.target.value })} />
    </label>
  );
  const sinp = (o: string, k: string, label: string, opts: { type?: string; span?: number; mono?: boolean; ph?: string; up?: boolean } = {}) => (
    <label style={opts.span ? { gridColumn: `span ${opts.span}` } : undefined}>
      {label}
      <input type={opts.type ?? 'text'} className={opts.mono ? 'mono' : ''} value={d[o]?.[k] ?? ''} disabled={ro} placeholder={opts.ph} onChange={(e) => sub(o, { [k]: opts.up ? e.target.value.toUpperCase() : e.target.value })} />
    </label>
  );
  const ssnBox = (
    <label>
      Social Security #
      {ro ? (
        <input value={ssn ?? ''} disabled />
      ) : (
        <input
          value={ssnText}
          inputMode="numeric"
          placeholder={ssn ?? '000-00-0000'}
          onChange={(e) => {
            setSsnText(e.target.value);
            const digits = e.target.value.replace(/\D/g, '');
            if (digits.length === 9 || digits.length === 0) sv.queue({ ssn: digits || null });
          }}
          aria-label="Social Security number"
        />
      )}
      <span className="small muted">Kept hidden (***-**-1234); showing it in full is logged.</span>
    </label>
  );
  const caseBox = (
    <label>
      Case #
      <input className="mono" value={cn} disabled={ro} onChange={(e) => (setCn(e.target.value), sv.queue({ caseNumber: e.target.value || null }))} />
    </label>
  );
  const sig = (slot: string, label: string, small?: boolean, name?: string) => <SignBox formId={f.id} slot={slot} label={label} image={signatures[slot]?.image} onSaved={reload} small={small} disabled={ro} name={name} />;
  const finishBtn = can.finish && (
    <div className="stack sm">
      <ErrorBox error={fin.error} />
      <div className="row gap-sm">
        <button
          className="btn primary"
          disabled={fin.busy}
          onClick={() =>
            fin.run(async () => {
              await sv.flush();
              await api.post(`/api/forms/${f.id}/finish`);
              reload();
            })
          }
        >
          {f.kind === 'ncic' ? 'Send to the NCIC operator' : 'Finish the form'}
        </button>
        <span className="small muted">{sv.state === 'saving' ? 'Saving…' : sv.state === 'saved' ? 'Saved' : sv.state === 'error' ? 'Not saved — check your signal' : ''}</span>
      </div>
    </div>
  );

  if (f.kind === 'rights')
    return (
      <>
        <Card title="Rights form" actions={!ro && <LicenseScanButton small onScan={(d) => upd({ name: [d.first, d.middle, d.last].filter(Boolean).join(' '), address: d.address ?? d['address'], dob: d.dob })} />}>
          <div className="grid-3">
            {inp('name', 'Name')}
            {inp('address', 'Address', { span: 2 })}
            {inp('dob', 'Date of birth', { type: 'date' })}
            {ssnBox}
            {caseBox}
            {inp('location', 'Location of Miranda', { span: 2, ph: 'Where you read the rights' })}
            {inp('readAt', 'Date / time', { type: 'datetime-local' })}
          </div>
        </Card>
        <Card title="Read each right; they initial each one">
          <ol className="fm-rights">
            {RIGHTS_LINES.map((line, i) => (
              <li key={i}>
                <span>{line}</span>
                {!d.refusedInitials && sig(`i${i + 1}`, 'Initials', true)}
              </li>
            ))}
          </ol>
          <label className="check">
            <input type="checkbox" checked={!!d.refusedInitials} disabled={ro} onChange={(e) => upd({ refusedInitials: e.target.checked })} /> They refused to initial
          </label>
        </Card>
        <Card title="What did they decide?">
          <div className="stack xs" role="radiogroup" aria-label="What they decided">
            {RIGHTS_OUTCOMES.map((o) => (
              <label key={o.code} className="check">
                <input type="radio" name="outcome" checked={d.outcome === o.code} disabled={ro} onChange={() => upd({ outcome: o.code })} /> {o.label}
              </label>
            ))}
          </div>
          {d.outcome && d.outcome !== 'waived' && (
            <label>
              Note
              <input value={d.note ?? ''} disabled={ro} onChange={(e) => upd({ note: e.target.value })} placeholder="What they said; questioning stopped at…" />
            </label>
          )}
          {(d.outcome === 'waived' || signatures.subject) && (
            <div className="fm-waiver">
              <h4>Waiver of rights</h4>
              <p>{RIGHTS_WAIVER}</p>
              {sig('subject', 'Their signature')}
            </div>
          )}
        </Card>
        <Card title="Witnesses">
          <div className="grid-2">
            <div className="stack sm">
              {inp('witness1', 'Witness (printed name)')}
              {sig('witness1', 'Witness signature', false, d.witness1)}
            </div>
            <div className="stack sm">
              {inp('witness2', 'Second witness (printed name)')}
              {sig('witness2', 'Second witness signature', false, d.witness2)}
            </div>
          </div>
        </Card>
        {finishBtn}
      </>
    );

  if (f.kind === 'custodial')
    return (
      <>
        <Card title="Custodial promise" actions={!ro && <LicenseScanButton small label="Scan parent’s license" onScan={(d) => upd({ parentName: [d.first, d.last].filter(Boolean).join(' '), custodianName: [d.first, d.last].filter(Boolean).join(' ') })} />}>
          <div className="grid-3">
            {caseBox}
            {inp('childName', 'Child’s name', { span: 2 })}
            {inp('childDob', 'Child’s DOB', { type: 'date' })}
            {ssnBox}
            {inp('childAddress', 'Child’s address')}
            {inp('parentName', 'Parent’s name')}
            {inp('parentPhone', 'Parent’s phone')}
          </div>
        </Card>
        <Card title="The promise">
          <div className="grid-2">
            {inp('custodianName', 'I, (parent / guardian / relative’s name)')}
            {inp('relationship', 'Relationship to the child', { ph: 'Mother, grandfather…' })}
          </div>
          <p className="fm-quote">“I, {d.custodianName || '________'}, {CUSTODIAL_PROMISE_TEXT}”</p>
          <label>
            Charge(s), one per line
            <textarea
              rows={3}
              disabled={ro}
              value={(d.charges ?? []).join('\n')}
              onChange={(e) => upd({ charges: e.target.value.split('\n') })}
              onBlur={() => upd({ charges: (d.charges ?? []).map((x: string) => x.trim()).filter(Boolean) })}
            />
          </label>
          {!ro && <AddChargePicker onAdd={(t) => upd({ charges: [...(d.charges ?? []).filter((x: string) => x.trim()), t] })} />}
          <div className="grid-2">
            {sig('custodian', 'Custodian’s signature', false, d.custodianName)}
            <div className="stack sm">
              {inp('witness', 'Witness (printed name)')}
              {sig('witness', 'Witness signature', false, d.witness)}
            </div>
          </div>
        </Card>
        {finishBtn}
      </>
    );

  // NCIC
  const p = d.person ?? {};
  const pt = d.propertyType;
  const show = (k: string) =>
    ({
      vinOrPlate: ['vehicle', 'plate'],
      state: ['vehicle', 'plate', 'boat'],
      regYear: ['vehicle', 'plate'],
      expYear: ['vehicle', 'plate'],
      year: ['vehicle', 'boat'],
      make: ['vehicle', 'boat', 'gun', 'article'],
      type: ['boat', 'gun', 'article'],
      model: ['vehicle', 'gun', 'article'],
      style: ['vehicle'],
      color: ['vehicle', 'boat', 'article'],
      brand: ['gun', 'article'],
      caliber: ['gun'],
      serial: ['gun', 'article', 'securities'],
      hull: ['boat'],
      nic: ['vehicle', 'gun', 'boat', 'plate', 'article', 'securities'],
      denomination: ['securities'],
      issuer: ['securities'],
      securitiesDate: ['securities'],
      theftDate: ['vehicle', 'gun', 'boat', 'plate', 'article', 'securities'],
    })[k]?.includes(pt) ?? false;
  return (
    <>
      <Card title={f.ncic_action === 'deletion' ? 'NCIC deletion request' : 'NCIC entry request'}>
        <div className="grid-3">
          {inp('ori', 'ORI number', { mono: true, ph: 'SC0120000' })}
          {caseBox}
        </div>
      </Card>
      <Card title="Person" actions={!ro && <LicenseScanButton small onScan={(dl) => sub('person', { name: [dl.last, dl.first, dl.middle].filter(Boolean).join(', '), sex: dl.sex, dob: dl.dob, ht: dl.heightInches ? `${Math.floor(dl.heightInches / 12)}${String(dl.heightInches % 12).padStart(2, '0')}` : p.ht, wt: dl.weight ? String(dl.weight) : p.wt, hair: dl.hair ?? p.hair, eyes: dl.eyes ?? p.eyes })} />}>
        <div className="row gap wrap" role="radiogroup" aria-label="Person is">
          {NCIC_PERSON_KINDS.map((k) => (
            <label key={k.code} className="check">
              <input type="radio" name="pk" checked={d.personKind === k.code} disabled={ro} onChange={() => upd({ personKind: k.code })} /> {k.label}
            </label>
          ))}
          {d.personKind && !ro && (
            <button type="button" className="btn small ghost" onClick={() => upd({ personKind: null })}>
              No person
            </button>
          )}
        </div>
        {(d.personKind || p.name) && (
          <div className="grid-4">
            {sinp('person', 'name', 'Name (last, first, middle)', { span: 2 })}
            {sinp('person', 'race', 'Race')}
            {sinp('person', 'sex', 'Sex')}
            {sinp('person', 'age', 'Age')}
            {sinp('person', 'dob', 'DOB', { type: 'date' })}
            {sinp('person', 'ht', 'Height', { ph: '510' })}
            {sinp('person', 'wt', 'Weight')}
            {sinp('person', 'hair', 'Hair')}
            {sinp('person', 'eyes', 'Eyes')}
            {sinp('person', 'skin', 'Skin')}
            {sinp('person', 'pob', 'Place of birth')}
            {sinp('person', 'warrants', 'Warrants', { span: 2 })}
            {sinp('person', 'charges', 'Charges', { span: 2 })}
            {!ro && (
              <div style={{ gridColumn: 'span 2' }}>
                <AddChargePicker onAdd={(t) => sub('person', { charges: [p.charges, t].filter(Boolean).join('; ') })} />
              </div>
            )}
            {d.personKind === 'wanted' && (
              <div className="stack xs">
                <span className="small strong">Extradite</span>
                <span className="row gap-sm">
                  <label className="check">
                    <input type="radio" name="ex" checked={p.extradite === true} disabled={ro} onChange={() => sub('person', { extradite: true })} /> Yes
                  </label>
                  <label className="check">
                    <input type="radio" name="ex" checked={p.extradite === false} disabled={ro} onChange={() => sub('person', { extradite: false })} /> No
                  </label>
                </span>
              </div>
            )}
            {d.personKind === 'wanted' && sinp('person', 'extraditeLimits', 'Extradition limits')}
            {sinp('person', 'date', 'Date (missing since / warrant date)', { type: 'date' })}
            {sinp('person', 'doe', 'Date of emancipation (DOE)', { type: 'date' })}
            {sinp('person', 'marks', 'Facial hair, scars, tattoos, glasses, clothing, peculiarities', { span: 4 })}
            {ssnBox}
          </div>
        )}
      </Card>
      <Card title="Property">
        <div className="grid-2">
          <label>
            Type
            <select value={pt ?? ''} disabled={ro} onChange={(e) => upd({ propertyType: e.target.value || null })}>
              <option value="">No property</option>
              {NCIC_PROPERTY_TYPES.map((t) => (
                <option key={t.code} value={t.code}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Status
            <select value={d.propertyStatus ?? ''} disabled={ro || !pt} onChange={(e) => upd({ propertyStatus: e.target.value || null })}>
              <option value="">—</option>
              {NCIC_PROPERTY_STATUS.map((t) => (
                <option key={t.code} value={t.code}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        {pt && (
          <div className="grid-4">
            {show('vinOrPlate') && sinp('property', 'vinOrPlate', pt === 'plate' ? 'License no.' : 'VIN and/or license no.', { span: 2, mono: true, up: true })}
            {show('state') && (
              <label>
                State
                <select value={d.property?.state ?? ''} disabled={ro} onChange={(e) => sub('property', { state: e.target.value })}>
                  <option value="">—</option>
                  {US_STATES.map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
              </label>
            )}
            {show('hull') && sinp('property', 'hull', 'Boat hull no.', { mono: true })}
            {show('serial') && sinp('property', 'serial', 'Serial and/or owner applied no.', { mono: true, span: 2 })}
            {show('regYear') && sinp('property', 'regYear', 'Year of registration')}
            {show('expYear') && sinp('property', 'expYear', 'Year of expiration')}
            {show('year') && sinp('property', 'year', 'Year')}
            {show('make') && sinp('property', 'make', 'Make')}
            {show('type') && sinp('property', 'type', 'Type')}
            {show('model') && sinp('property', 'model', 'Model')}
            {show('style') && sinp('property', 'style', 'Style')}
            {show('color') && sinp('property', 'color', 'Color')}
            {show('brand') && sinp('property', 'brand', 'Brand name')}
            {show('caliber') && sinp('property', 'caliber', 'Caliber')}
            {show('denomination') && sinp('property', 'denomination', 'Denomination')}
            {show('issuer') && sinp('property', 'issuer', 'Issuer')}
            {show('securitiesDate') && sinp('property', 'securitiesDate', 'Securities date', { type: 'date' })}
            {show('nic') && sinp('property', 'nic', 'NIC no.', { mono: true })}
            {show('theftDate') && sinp('property', 'theftDate', 'Date of theft', { type: 'date' })}
          </div>
        )}
        {pt === 'boat' && (
          <div className="grid-5" style={{ marginTop: 8 }}>
            {sinp('boat', 'reg', 'REG')}
            {sinp('boat', 'hul', 'HUL')}
            {sinp('boat', 'bhn', 'BHN')}
            {sinp('boat', 'pro', 'PRO')}
            {sinp('boat', 'byr', 'BYR')}
            {sinp('boat', 'bma', 'BMA')}
            {sinp('boat', 'bty', 'BTY')}
            {sinp('boat', 'ble', 'BLE')}
            {sinp('boat', 'bco', 'BCO')}
          </div>
        )}
      </Card>
      <Card title="Remarks">
        <textarea rows={3} value={d.remarks ?? ''} disabled={ro} onChange={(e) => upd({ remarks: e.target.value })} aria-label="Remarks" />
      </Card>
      {finishBtn}
    </>
  );
}

/** Add a charge from the SC list (statute and words). */
function AddChargePicker({ onAdd }: { onAdd: (text: string) => void }) {
  const [q, setQ] = useState('');
  return (
    <label>
      Add a charge from the list
      <StatutePicker value={q} label="Add a charge from the list" onText={setQ} onPick={(c) => (onAdd(`${c.statute} ${chargeWords(c.description)}`.trim()), setQ(''))} />
    </label>
  );
}

// ─── Printed like the paper forms ──────────────────────────────────────────
export function FormPrint({ id }: { id: string }) {
  const [reveal, setReveal] = useState(false);
  const r = useLoad(() => api.get<any>(`/api/forms/${id}${reveal ? '?reveal=ssn' : ''}`), [id, reveal]);
  if (!r.data) return r.error ? <ErrorBox error={r.error} /> : <Spinner />;
  const { form: f, signatures: sg, agency, officer, operator, ssn, can } = r.data;
  const d = f.data ?? {};
  const tools =
    can.revealSsn && !reveal ? (
      <button className="btn" onClick={() => setReveal(true)}>
        Print the full SSN
      </button>
    ) : null;
  const draft = f.status === 'draft' ? <div className="pf-stamp">DRAFT — NOT FINISHED</div> : f.status === 'void' ? <div className="pf-stamp">VOID</div> : null;
  if (f.kind === 'rights') {
    const outcome = RIGHTS_OUTCOMES.find((o) => o.code === d.outcome);
    return (
      <PrintSheet label={`Rights form ${f.number}`} tools={tools}>
        {draft}
        <div className="pf-center">
          <div className="pf-agname">{agency.name}</div>
          <h1 className="pf-h1">Rights Form</h1>
          <div className="pf-read">**READ CAREFULLY**
            <br />
            **THESE ARE YOUR RIGHTS**
          </div>
        </div>
        <div className="pf-fill">
          <div>
            <b>NAME:</b> <u>{d.name}</u>
          </div>
          <div>
            <b>ADDRESS:</b> <u>{d.address}</u>
          </div>
          <div className="pf-two">
            <span>
              <b>SOCIAL SECURITY #</b> <u className="mono">{ssn ?? ''}</u>
            </span>
            <span>
              <b>DATE OF BIRTH:</b> <u>{fmtD(d.dob)}</u>
            </span>
          </div>
          <div>
            <b>LOCATION OF MIRANDA:</b> <u>{d.location}</u>
          </div>
          <div className="pf-two">
            <span>
              <b>DATE:</b> <u>{fmtD(d.readAt)}</u>
            </span>
            <span>
              <b>TIME:</b> <u>{fmtT(d.readAt)}</u>
            </span>
          </div>
        </div>
        <ol className="pf-rights">
          {RIGHTS_LINES.map((l, i) => (
            <li key={i}>
              <span>
                {i + 1}) {l.toUpperCase()}
              </span>
              <span className="pf-init">{sg[`i${i + 1}`] ? <img src={sg[`i${i + 1}`].image} alt={`Initials ${i + 1}`} /> : d.refusedInitials ? <em>refused</em> : null}</span>
            </li>
          ))}
        </ol>
        <h2 className="pf-h2 u">Waiver of Rights</h2>
        <p className="pf-waiver">{RIGHTS_WAIVER.toUpperCase()}</p>
        {outcome && outcome.code !== 'waived' && (
          <p className="pf-outcome">
            <b>Did not waive:</b> {outcome.label}
            {d.note ? ` — ${d.note}` : ''}
          </p>
        )}
        <div className="pf-sigs">
          <Line caption="SIGNATURE" image={sg.subject?.image} text={!sg.subject && d.outcome === 'refused_sign' ? 'Refused to sign' : undefined} date={sg.subject ? fmtD(sg.subject.signedAt) : ''} wide />
          <Line caption={`WITNESS${d.witness1 ? ` — ${d.witness1}` : ''}`} image={sg.witness1?.image} date={sg.witness1 ? fmtD(sg.witness1.signedAt) : ''} wide />
          <Line caption={`WITNESS${d.witness2 ? ` — ${d.witness2}` : ''}`} image={sg.witness2?.image} date={sg.witness2 ? fmtD(sg.witness2.signedAt) : ''} wide />
        </div>
        <p className="pf-foot">
          {f.number}
          {f.case_number ? ` · Case ${f.case_number}` : ''} · Rights read by {officer?.name}
          {officer?.badge ? ` (${officer.badge})` : ''} · signed on screen in BlueHQ
        </p>
      </PrintSheet>
    );
  }
  if (f.kind === 'custodial') {
    return (
      <PrintSheet label={`Custodial promise ${f.number}`} tools={tools}>
        {draft}
        <div className="pf-center">
          <div className="pf-agname">{agency.name}</div>
          <h1 className="pf-h1">Custodial Promise</h1>
        </div>
        <div className="pf-fill">
          <div>
            <b>Case #</b> <u className="mono">{f.case_number}</u>
          </div>
          <div>
            <b>Child’s Name:</b> <u>{d.childName}</u>
          </div>
          <div className="pf-two">
            <span>
              <b>Child’s DOB:</b> <u>{fmtD(d.childDob)}</u>
            </span>
            <span>
              <b>Child’s SS#</b> <u className="mono">{ssn ?? ''}</u>
            </span>
          </div>
          <div>
            <b>Child’s Address:</b> <u>{d.childAddress}</u>
          </div>
          <div>
            <b>Parent’s Name:</b> <u>{d.parentName}</u>
          </div>
          <div>
            <b>Parent’s Phone Number:</b> <u>{d.parentPhone}</u>
          </div>
        </div>
        <p className="pf-promise">
          I, <u className="pf-blank">{d.custodianName}</u>
          {d.relationship ? ` (${d.relationship})` : ''}, {CUSTODIAL_PROMISE_TEXT}
        </p>
        <div className="pf-sigs">
          <Line caption="Custodian’s Signature" image={sg.custodian?.image} date={sg.custodian ? fmtD(sg.custodian.signedAt) : ''} wide />
          <Line caption={`Witness Signature${d.witness ? ` — ${d.witness}` : ''}`} image={sg.witness?.image} date={sg.witness ? fmtD(sg.witness.signedAt) : ''} wide />
        </div>
        <div className="pf-charges">
          <b>Charge(s):</b>
          {[...(d.charges ?? []), '', '', '', ''].slice(0, Math.max(4, (d.charges ?? []).length)).map((c: string, i: number) => (
            <div key={i} className="pf-rule">
              {c}
            </div>
          ))}
        </div>
        <p className="pf-foot">
          {f.number} · {officer?.name}
          {officer?.badge ? ` (${officer.badge})` : ''} · signed on screen in BlueHQ
        </p>
      </PrintSheet>
    );
  }
  const p = d.person ?? {};
  const pr = d.property ?? {};
  const bt = d.boat ?? {};
  const boxes = (s: string | null | undefined, n: number, lead = '') => {
    const chars = (lead + (s ?? '').slice(lead.length)).padEnd(n, ' ').slice(0, n).split('');
    return (
      <span className="pf-cells">
        {chars.map((c, i) => (
          <span key={i}>{c.trim() || ' '}</span>
        ))}
      </span>
    );
  };
  return (
    <PrintSheet label={`NCIC request ${f.number}`} tools={tools}>
      {draft}
      <div className="pf-center">
        <div className="pf-agline">{agency.name}</div>
        <div className="pf-small">(LAW ENFORCEMENT AGENCY)</div>
        <h1 className="pf-h1 it">Request for NCIC {f.ncic_action === 'deletion' ? 'Deletion' : 'Entry'}</h1>
        <div className="pf-small">{f.ncic_action === 'deletion' ? 'ENTRY/☒DELETION' : '☒ENTRY/DELETION'}</div>
      </div>
      <div className="pf-row between">
        <div>
          <div className="pf-l">ORI number</div>
          {boxes(d.ori, 9, 'SC0')}
        </div>
        <div>
          <div className="pf-l">Case number</div>
          {boxes(f.case_number, 11)}
        </div>
      </div>
      <h2 className="pf-h2">Persons</h2>
      <div className="pf-ncic-p">
        <div className="pf-kinds">
          {NCIC_PERSON_KINDS.map((k) => (
            <Check key={k.code} on={d.personKind === k.code}>
              {k.label.toUpperCase()}
            </Check>
          ))}
        </div>
        <div className="pf-grid c8">
          <Box l="Name (last, first, middle)" v={p.name} span={8} />
          <Box l="Race" v={p.race} />
          <Box l="Sex" v={p.sex} />
          <Box l="Age" v={p.age} />
          <Box l="DOB" v={fmtD(p.dob)} />
          <Box l="HT" v={p.ht} />
          <Box l="WT" v={p.wt} />
          <Box l="Hair" v={p.hair} />
          <Box l="Eyes" v={p.eyes} />
          <Box l="Warrants" v={p.warrants} span={8} />
          <Box l="Charges" v={p.charges} span={5} />
          <Box
            l="Extradite"
            v={
              <>
                <Check on={p.extradite === true}>YES</Check> <Check on={p.extradite === false}>NO</Check> {p.extraditeLimits}
              </>
            }
            span={3}
          />
          <Box l="Date" v={fmtD(p.date)} span={2} />
          <Box l="Facial hair, scars, tattoos, glasses, clothing, physical peculiarities, etc." v={p.marks} span={6} />
          <Box l="DOE" v={fmtD(p.doe)} span={2} />
          <Box l="POB" v={p.pob} span={2} />
          <Box l="Skin" v={p.skin} span={2} />
          <Box l="SSN" v={ssn} span={2} mono />
        </div>
      </div>
      <h2 className="pf-h2">Property</h2>
      <div className="pf-ncic-p wide">
        <div className="pf-kinds two">
          <div>
            <b>STATUS</b>
            {NCIC_PROPERTY_STATUS.map((k) => (
              <Check key={k.code} on={d.propertyStatus === k.code}>
                {k.label.toUpperCase()}
              </Check>
            ))}
          </div>
          <div>
            <b>TYPE</b>
            {NCIC_PROPERTY_TYPES.map((k) => (
              <Check key={k.code} on={d.propertyType === k.code}>
                {k.label.toUpperCase()}
              </Check>
            ))}
          </div>
        </div>
        <div className="pf-grid c8">
          <Box l="VIN and/or license no." v={pr.vinOrPlate} span={4} mono />
          <Box l="Boat hull no. and/or reg. no." v={pr.hull} span={4} mono />
          <Box l="Serial and/or owner applied no." v={pr.serial} span={6} mono />
          <Box l="State" v={pr.state} span={2} />
          <Box l="Year of registration" v={pr.regYear} span={2} />
          <Box l="Year of expiration" v={pr.expYear} span={2} />
          <Box l="Year" v={pr.year} />
          <Box l="Make" v={pr.make} />
          <Box l="Type" v={pr.type} span={2} />
          <Box l="Model" v={pr.model} span={2} />
          <Box l="Style" v={pr.style} />
          <Box l="Color" v={pr.color} />
          <Box l="Brand name" v={pr.brand} span={2} />
          <Box l="Caliber" v={pr.caliber} span={2} />
          <Box l="NIC no." v={pr.nic} span={2} mono />
          <Box l="Denomination" v={pr.denomination} span={2} />
          <Box l="Issuer" v={pr.issuer} span={2} />
          <Box l="Securities date" v={fmtD(pr.securitiesDate)} span={2} />
          <Box l="Date of theft" v={fmtD(pr.theftDate)} span={8} />
        </div>
      </div>
      <div className="pf-boat">
        <b>BOAT</b>
        {(['reg', 'hul', 'bhn', 'pro', 'byr', 'bma', 'bty', 'ble', 'bco'] as const).map((k) => (
          <span key={k}>
            {k.toUpperCase()} <u>{bt[k] ?? ''}</u>
          </span>
        ))}
      </div>
      <div className="pf-remarks">
        <span className="pf-l">Remarks - misc</span>
        <div>{d.remarks}</div>
      </div>
      <div className="pf-grid c4 pf-sigrow">
        <Box l="Requesting officer signature" v={f.completed_at ? <span className="pf-esig">/s/ {officer?.name}{officer?.badge ? ` (${officer.badge})` : ''}</span> : ''} span={2} />
        <Box l="Date" v={fmtDT(f.completed_at)} />
        <Box l="NIC number" v={f.nic_number} mono />
        <Box l="Computer operator signature" v={f.operator_at && f.status === 'entered' ? <span className="pf-esig">/s/ {operator?.name}</span> : f.status === 'rejected' ? `Sent back: ${f.operator_note}` : ''} span={3} />
        <Box l="Date" v={f.status === 'entered' ? fmtDT(f.operator_at) : ''} />
      </div>
      <p className="pf-foot">
        {f.number} · /s/ = signed electronically in BlueHQ when the request was sent and when it was entered
      </p>
    </PrintSheet>
  );
}
